import type { GoogleDriveBackupAdapter } from './drive.js';
import type { IndexedDbSalonStore } from './indexeddb-store.js';

export const AUTO_BACKUP_INTERVAL_MS = 24 * 60 * 60 * 1000;
export const AUTO_BACKUP_RETRY_DELAYS_MS = [60_000, 5 * 60_000, 15 * 60_000, 60 * 60_000] as const;

const META = {
  enabled: 'backup.auto.enabled',
  lastAttemptAt: 'backup.auto.lastAttemptAt',
  nextAttemptAt: 'backup.auto.nextAttemptAt',
  retryCount: 'backup.auto.retryCount',
  error: 'backup.auto.error'
} as const;

export interface AutoDriveBackupError { code: string; at: string; }
export interface AutoDriveBackupStatus {
  enabled: boolean;
  writer: boolean;
  connected: boolean;
  sessionUnlocked: boolean;
  pending: boolean;
  running: boolean;
  lastAttemptAt: string | null;
  lastSuccessAt: string | null;
  nextAttemptAt: string | null;
  retryCount: number;
  error: AutoDriveBackupError | null;
}

export interface AutoBackupEnvironment {
  now(): number;
  online(): boolean;
  setTimer(callback: () => void, delayMs: number): unknown;
  clearTimer(handle: unknown): void;
  onOnline(callback: () => void): () => void;
}

const browserEnvironment = (): AutoBackupEnvironment => ({
  now: () => Date.now(),
  online: () => navigator.onLine,
  setTimer: (callback, delayMs) => window.setTimeout(callback, delayMs),
  clearTimer: (handle) => window.clearTimeout(handle as number),
  onOnline: (callback) => { addEventListener('online', callback); return () => removeEventListener('online', callback); }
});

export class AutoDriveBackupCoordinator {
  private enabled = false;
  private passphrase: string | null = null;
  private timer: unknown = null;
  private inFlight: Promise<void> | null = null;
  private removeOnlineListener: (() => void) | null = null;
  private started = false;

  constructor(
    private readonly store: Pick<IndexedDbSalonStore, 'isWriter'|'getMeta'|'setMeta'>,
    private readonly drive: Pick<GoogleDriveBackupAdapter, 'connected'|'backupNow'>,
    private readonly environment: AutoBackupEnvironment = browserEnvironment()
  ) {}

  async start(): Promise<void> {
    if (this.started) return;
    this.started = true;
    this.enabled = await this.store.getMeta<boolean>(META.enabled) ?? false;
    this.removeOnlineListener = this.environment.onOnline(() => { void this.reconcile(); });
    if (this.store.isWriter) await this.reconcile();
  }

  stop(): void {
    this.passphrase = null;
    this.cancelTimer();
    this.removeOnlineListener?.();
    this.removeOnlineListener = null;
  }

  async enable(passphrase: string): Promise<void> {
    this.requireWriter();
    this.validatePassphrase(passphrase);
    this.passphrase = passphrase;
    this.enabled = true;
    await this.store.setMeta(META.enabled, true);
    await this.resetRetry();
    await this.reconcile(false);
  }

  async unlock(passphrase: string): Promise<void> {
    this.requireWriter();
    if (!this.enabled) throw new Error('AUTO_BACKUP_DISABLED');
    this.validatePassphrase(passphrase);
    this.passphrase = passphrase;
    await this.clearError();
    await this.reconcile(false);
  }

  async disable(): Promise<void> {
    this.requireWriter();
    this.enabled = false;
    this.passphrase = null;
    this.cancelTimer();
    await this.store.setMeta(META.enabled, false);
    await this.resetRetry();
    await this.clearError();
  }

  async driveDisconnected(): Promise<void> {
    this.passphrase = null;
    this.cancelTimer();
    if (this.enabled && this.store.isWriter) await this.blocked('DRIVE_AUTH_REQUIRED');
  }

  async runManual(passphrase: string): Promise<void> {
    this.requireWriter();
    this.validatePassphrase(passphrase);
    if (this.enabled) this.passphrase = passphrase;
    await this.attempt(passphrase, false);
  }

  async status(): Promise<AutoDriveBackupStatus> {
    const [persistedEnabled, lastAttemptAt, lastSuccessAt, nextAttemptAt, retryCount, error] = await Promise.all([
      this.store.getMeta<boolean>(META.enabled),
      this.store.getMeta<string>(META.lastAttemptAt),
      this.store.getMeta<string>('backup.lastDriveAt'),
      this.store.getMeta<string>(META.nextAttemptAt),
      this.store.getMeta<number>(META.retryCount),
      this.store.getMeta<AutoDriveBackupError>(META.error)
    ]);
    const enabled = persistedEnabled ?? this.enabled;
    return {
      enabled,
      writer: this.store.isWriter,
      connected: this.drive.connected,
      sessionUnlocked: Boolean(this.passphrase),
      pending: enabled && this.isDue(lastSuccessAt),
      running: Boolean(this.inFlight),
      lastAttemptAt,
      lastSuccessAt,
      nextAttemptAt,
      retryCount: retryCount ?? 0,
      error
    };
  }

  private async reconcile(suppressAttemptError = true): Promise<void> {
    if (!this.store.isWriter || !this.enabled || this.inFlight) return;
    const lastSuccessAt = await this.store.getMeta<string>('backup.lastDriveAt');
    if (!this.isDue(lastSuccessAt)) {
      const dueAt = new Date(new Date(lastSuccessAt!).getTime() + AUTO_BACKUP_INTERVAL_MS).toISOString();
      await this.schedule(dueAt);
      return;
    }
    if (!this.drive.connected) { await this.blocked('DRIVE_AUTH_REQUIRED'); return; }
    if (!this.passphrase) { await this.blocked('AUTO_BACKUP_PASSPHRASE_REQUIRED'); return; }
    const attempt = this.attempt(this.passphrase, true);
    if (suppressAttemptError) await attempt.catch(() => undefined); else await attempt;
  }

  private async attempt(passphrase: string, automatic: boolean): Promise<void> {
    if (this.inFlight) return this.inFlight;
    const run = async (): Promise<void> => {
      const attemptAt = new Date(this.environment.now()).toISOString();
      await this.store.setMeta(META.lastAttemptAt, attemptAt);
      await this.store.setMeta(META.nextAttemptAt, null);
      try {
        if (!this.environment.online()) throw new Error('DRIVE_OFFLINE_PENDING');
        await this.drive.backupNow(passphrase);
        await this.resetRetry();
        await this.clearError();
        const successAt = await this.store.getMeta<string>('backup.lastDriveAt') ?? attemptAt;
        await this.schedule(new Date(new Date(successAt).getTime() + AUTO_BACKUP_INTERVAL_MS).toISOString());
      } catch (error) {
        const code = error instanceof Error ? error.message : 'DRIVE_BACKUP_FAILED';
        await this.store.setMeta(META.error, { code, at: new Date(this.environment.now()).toISOString() } satisfies AutoDriveBackupError);
        if ((automatic || this.enabled) && this.isTransient(code)) await this.scheduleRetry();
        throw error;
      }
    };
    this.inFlight = run().finally(() => { this.inFlight = null; });
    return this.inFlight;
  }

  private async blocked(code: string): Promise<void> {
    this.cancelTimer();
    await this.store.setMeta(META.nextAttemptAt, null);
    await this.store.setMeta(META.error, { code, at: new Date(this.environment.now()).toISOString() } satisfies AutoDriveBackupError);
  }

  private async scheduleRetry(): Promise<void> {
    const current = await this.store.getMeta<number>(META.retryCount) ?? 0;
    const delay = AUTO_BACKUP_RETRY_DELAYS_MS[current];
    if (delay === undefined) { await this.store.setMeta(META.nextAttemptAt, null); return; }
    await this.store.setMeta(META.retryCount, current + 1);
    await this.schedule(new Date(this.environment.now() + delay).toISOString());
  }

  private async schedule(at: string): Promise<void> {
    this.cancelTimer();
    await this.store.setMeta(META.nextAttemptAt, at);
    const delay = Math.max(0, new Date(at).getTime() - this.environment.now());
    this.timer = this.environment.setTimer(() => { this.timer = null; void this.reconcile(); }, delay);
  }

  private cancelTimer(): void { if (this.timer !== null) this.environment.clearTimer(this.timer); this.timer = null; }
  private async resetRetry(): Promise<void> { await this.store.setMeta(META.retryCount, 0); await this.store.setMeta(META.nextAttemptAt, null); }
  private async clearError(): Promise<void> { await this.store.setMeta(META.error, null); }
  private isDue(lastSuccessAt: string | null): boolean { return !lastSuccessAt || this.environment.now() - new Date(lastSuccessAt).getTime() >= AUTO_BACKUP_INTERVAL_MS; }
  private isTransient(code: string): boolean { return code === 'DRIVE_OFFLINE_PENDING' || /^DRIVE_REQUEST_FAILED_(408|429|5\d\d)$/.test(code); }
  private validatePassphrase(passphrase: string): void { if (passphrase.length < 10) throw new Error('BACKUP_PASSPHRASE_TOO_SHORT'); }
  private requireWriter(): void { if (!this.store.isWriter) throw new Error('READ_ONLY_TAB'); }
}
